import { useCallback, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { AppIcon, ICON_SIZES, toolTransform } from '@backrs_ui/common-ui';

import { HoverButton } from '@backrs_ui/common-ui/src/molecules/Nugget/HoverButton';
import { APP_ROUTE } from '@backrs_ui/grow-ui/src/appRoutes.constants';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import competencyLibraryBanner from '@backrs_ui/grow-ui/src/assets/images/library/competency-library-banner.png';
import { getBookmarkIdForNugget } from '@backrs_ui/grow-ui/src/pages/Bookmarks/pages/bookmarks.util';
import {
  useDeleteBookmarkMutation,
  useGetBookmarksQuery,
  usePostBookmarkMutation,
} from '@backrs_ui/grow-ui/src/redux/bookmarks/bookmarks.api';
import { useAppDispatch } from '@backrs_ui/grow-ui/src/redux/hooks';
import {
  openPlaylistListStep,
  setNuggetToAdd,
} from '@backrs_ui/grow-ui/src/redux/priorities/priorities.slice';
import { useGetToolByToolIdQuery } from '@backrs_ui/grow-ui/src/redux/tools/tools.api';
import { GROW_URL } from '@backrs_ui/grow-ui/src/settings';

import { Box, Button, SxProps, Theme, Typography } from '@mui/material';

const LIBRARY_STRINGS = appStrings.modules.library;

const COMPETENCY_LIBRARY_TOOL_ID = 'competency_library';

export const ProsperHeader = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const dispatch = useAppDispatch();

  const { data: bookmarks } = useGetBookmarksQuery(undefined);
  const [postBookmark] = usePostBookmarkMutation();
  const [deleteBookmark] = useDeleteBookmarkMutation();
  const { data: libraryTool } = useGetToolByToolIdQuery(COMPETENCY_LIBRARY_TOOL_ID);

  const [isHovered, setIsHovered] = useState(false);

  const bookmarkId = libraryTool
    ? getBookmarkIdForNugget(bookmarks, libraryTool.id, 'tool')
    : undefined;
  const isBookmarked = !!bookmarkId;

  const handleNavigateToHowItWorks = () => {
    navigate(APP_ROUTE.SUCCESS_SYSTEM);
  };

  const handleBookmark = useCallback(async () => {
    if (!libraryTool) return;

    if (bookmarkId) {
      await deleteBookmark(bookmarkId);
    } else {
      await postBookmark({ type: 'tool', itemId: libraryTool.id });
    }
  }, [bookmarkId, deleteBookmark, postBookmark, libraryTool]);

  const handleShare = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(`${GROW_URL}${pathname}`);
    } catch (error) {
      console.error('Failed to copy link to clipboard:', error);
    }
  }, [pathname]);

  const handleAddTask = useCallback(() => {
    if (!libraryTool) return;

    dispatch(openPlaylistListStep());
    dispatch(setNuggetToAdd(toolTransform(libraryTool)));
  }, [dispatch, libraryTool]);

  return (
    <Box sx={bannerCardSx}>
      <Box
        sx={bannerImageWrapperSx}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <Box
          component="img"
          src={competencyLibraryBanner}
          alt={LIBRARY_STRINGS.header.title}
          sx={bannerImageSx}
        />
        {libraryTool && (
          <HoverButton
            isVisible={isHovered}
            idPrefix="prosper-header"
            hasBookmarked={isBookmarked}
            handleActionBookmark={handleBookmark}
            handleActionShare={handleShare}
            handleActionAddTask={handleAddTask}
          />
        )}
      </Box>
      <Box sx={bannerBodySx}>
        <Box sx={bannerTextSx}>
          <Typography variant="display-xs-semibold" sx={bannerTitleSx}>
            {LIBRARY_STRINGS.header.title}
          </Typography>
          <Typography variant="text-md-regular" sx={bannerSubtitleSx}>
            {LIBRARY_STRINGS.header.subtitle}
          </Typography>
        </Box>
        <Button
          id="prosper-how-it-works-button"
          hierarchy="Secondary gray"
          size="md"
          onClick={handleNavigateToHowItWorks}
          endIcon={
            <AppIcon iconName="InfoCircle" diameter={ICON_SIZES['20px']} color="currentColor" />
          }
          sx={builtOnFourUnlocksButtonSx}
        >
          {LIBRARY_STRINGS.header.builtOnFourUnlocks}
        </Button>
      </Box>
    </Box>
  );
};

const bannerCardSx: SxProps<Theme> = ({ backrs }) => ({
  overflow: 'hidden',
  width: '100%',
  borderRadius: backrs.radius['radius-xl'],
  border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
});

const bannerImageWrapperSx: SxProps<Theme> = {
  position: 'relative',
  width: '100%',
};

const bannerImageSx: SxProps<Theme> = {
  display: 'block',
  width: '100%',
  height: 'auto',
  objectFit: 'cover',
  objectPosition: 'center',
};

const bannerBodySx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: backrs.spacing['spacing-lg'],
  padding: backrs.spacing['spacing-2xl'],
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
  borderTop: `1px solid ${backrs.colors['Colors/Base/Black']}`,
  '@container (min-width: 600px)': {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
});

const bannerTextSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xs'],
});

const bannerTitleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
});

const bannerSubtitleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});

const builtOnFourUnlocksButtonSx: SxProps<Theme> = {
  whiteSpace: 'nowrap',
  flexShrink: 0,
};
